import { TextButton } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { typographyCss, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Option } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { getDefaultValueIndex } from 'apis/restaurant-api/merchant-admin/menu2/utils';
import { localize } from 'common/utils';

import OptionValue from './OptionValue';
import OptionValueAdvancedDetailsViewModal from './OptionValueAdvancedDetailsViewModal';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';

type Props = {
  language: Language;
  option: Option;
};

const Root = styled.div``;

const Section = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Heading = styled.div`
  ${typographyCss.Title1()};
  display: flex;
  align-items: center;
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const AdvancedDetailsModalToggleContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const optionTypeToTranslationStringId: { [key in Option['type']]: string } = {
  bool: 'menu-option.switch',
  choice: 'menu-option.single-choice',
  multi_choice: 'menu-option.multi-choice',
};

const optionAmountToTranslationStringId: { [key in Option['type']]: string } = {
  bool: 'menu-option.choices-one',
  choice: 'menu-option.choices-one',
  multi_choice: 'menu-option.choices-many',
};

const OptionReadFields = ({ language, option }: Props) => {
  const intl = useIntl();

  const [isAdvancedDetailsModalOpen, setIsAdvancedDetailsModalOpen] = useState(false);

  const isChoice = ['choice', 'multi_choice'].includes(option.type);
  const defaultValueIndex = getDefaultValueIndex(option);
  const isMultiChoiceOption = option.type === 'multi_choice';

  const menuPermissions = useContext(MenuPermissionsContext);

  return (
    <Root>
      <Section>
        <Heading>
          <FormattedMessage id="menu-option.details-header" />
        </Heading>
        <FormRow label={intl.formatMessage({ id: 'menu-option.option-type-label' })}>
          <FormattedMessage id={optionTypeToTranslationStringId[option.type]} />
        </FormRow>
        <FormRow
          label={intl.formatMessage({
            id: isChoice ? 'menu-option.choice-title-label' : 'menu-option.bool-title-label',
          })}
        >
          {localize(option.name, language)}
        </FormRow>
        {isChoice && (
          <FormRow label={intl.formatMessage({ id: 'menu-option.choices-amount-label' })}>
            <FormattedMessage id={optionAmountToTranslationStringId[option.type]} />
          </FormRow>
        )}
        <FormRow label={intl.formatMessage({ id: 'menu-option.show-to-courier' })}>
          {option.values.some(value => value.visible_to_courier) ? (
            <FormattedMessage id="boolean.true" />
          ) : (
            <FormattedMessage id="boolean.false" />
          )}
        </FormRow>
        {menuPermissions.posIdField && (
          <FormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>{option.external_id || '-'}</FormRow>
        )}
      </Section>
      <div>
        <Heading>
          <FormattedMessage id="menu-option.choices-header" />
        </Heading>
        {option.values.map((optionValue, index) => (
          <OptionValue
            language={language}
            optionId={option.id}
            optionValue={optionValue}
            isDefault={defaultValueIndex === index}
            isChoice={isChoice}
            isMultiChoice={isMultiChoiceOption}
            key={optionValue.id}
          />
        ))}
      </div>
      <AdvancedDetailsModalToggleContainer>
        <TextButton onClick={() => setIsAdvancedDetailsModalOpen(true)} variant="blue">
          <FormattedMessage id="menu-option.advanced-details" />
        </TextButton>
      </AdvancedDetailsModalToggleContainer>
      <OptionValueAdvancedDetailsViewModal
        isOpen={isAdvancedDetailsModalOpen}
        onClose={() => setIsAdvancedDetailsModalOpen(false)}
        optionValues={option.values}
      />
    </Root>
  );
};

export default OptionReadFields;
